import React, { FC, useEffect, useState, useMemo } from 'react';
import { Box, Text, AddressInputProps, MultiSelect } from '@wix/design-system';
import _ from 'lodash';
import { GetPlaceResponse } from '@wix/ambassador-atlas-v2-place/types';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import {
  ACTIONS,
  useExploreContext,
} from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams } from '@utils/route';
import { generateLocationLabel } from '@utils/locationLabel';
import { useBi } from '@components/Hooks/useBi';
import { useShallowRouter } from '@components/Hooks/useRouter';
import FilterPopover from '../FilterPopover';
import ExploreAddressInput from './ExploreAddressInput';
import s from './PartnerDetails.module.scss';

const PartnerDetails: FC = () => {
  const { t } = useTranslation();
  const logger = useBi();
  const { navigate, router } = useShallowRouter();

  const { sessionId } = useBiSessionContext();
  const {
    dispatch,
    state: {
      selectedLocation,
      languages,
      selectedLanguages,
      entry,
      origin,
      currentPage,
    },
  } = useExploreContext();

  const languageOptions = _.map(languages, (language) => ({
    id: language.id,
    value: language.value,
  }));

  const [languageInput, setLanguageInput] = useState<string>('');
  const [languageSelections, setLanguageSelections] = useState<
    Array<{ id: string; label: string }>
  >([]);

  const [location, setLocation] = useState<GetPlaceResponse['place'] | null>();
  const [locationInput, setLocationInput] = useState('');
  const [locationStatus, setLocationStatus] =
    useState<AddressInputProps['status']>();

  const applyOnClick = () => {
    const hasClearedLocationFilter = !locationInput;
    const selectedLocation = hasClearedLocationFilter
      ? ''
      : location?.placeId ?? '';

    const lang = languageSelections.map((lang) => lang.id).join(',');

    navigate(
      updateQueryParams(
        {
          lang,
          location: selectedLocation,
        },
        router,
      ),
    );
  };

  useEffect(() => {
    if (selectedLocation) {
      setLocation(selectedLocation);
      // updated this
      const locationInput = selectedLocation?.address
        ? generateLocationLabel(selectedLocation?.address)
        : '';
      setLocationInput(locationInput);
      setLocationStatus(undefined);
    } else {
      setLocation(null);
      setLocationInput('');
    }
  }, [selectedLocation]);

  const onSelectLocation = (selectedLocation: GetPlaceResponse['place']) => {
    setLocation(selectedLocation);
    const newLocationLabel = selectedLocation?.address
      ? generateLocationLabel(selectedLocation.address)
      : '';
    setLocationInput(newLocationLabel ?? '');
  };

  const onClearLocation = () => {
    setLocationInput('');
  };

  const onChangeLocation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocationInput(event.target.value);
  };

  const languageSelectPredicate = (option: any) =>
    option.value &&
    option.value.toLowerCase().includes(languageInput.toLowerCase());

  const onChangeLanguage = (event: any) => setLanguageInput(event.target.value);

  const onSelectLanguage = (tag: any) =>
    setLanguageSelections((currentTags) => [
      ...currentTags,
      { id: tag.id, label: tag.value, size: 'tiny' },
    ]);

  const onRemoveLanguageTag = (tagId: any) =>
    setLanguageSelections((currentTags) =>
      currentTags.filter((currTag) => currTag.id !== tagId),
    );

  useEffect(() => {
    const formattedLanguages = _.map(selectedLanguages, (language) => ({
      id: language.id,
      label: language.value,
      size: 'tiny',
    }));
    setLanguageSelections(formattedLanguages);
  }, [selectedLanguages]);

  const numSelectedFilters = useMemo(() => {
    const locationAdder = location && locationInput !== '' ? 1 : 0;
    const languageAdder = _.size(languageSelections);
    return locationAdder + languageAdder;
  }, [languageSelections, location, locationInput]);

  return (
    <FilterPopover
      numSelectedServices={numSelectedFilters}
      element={t('explorer-filter-professional-details')}
      panel="professional-details"
      minWidth={255}
      onClear={() => {
        logger.report(
          exploreResultsPageGenericClick({
            clickType: 'clear all location/language filters',
            sessionId,
            origin,
            entry,
            exploreVersion: 'V2',
            data: JSON.stringify({
              currentPage,
            }),
          }),
        );
        setLanguageInput('');
        setLanguageSelections([]);
        setLocation(null);
        setLocationInput('');
        setLocationStatus(undefined);
      }}
      onApply={() => {
        applyOnClick();
        dispatch({
          type: ACTIONS.FILTER_POPOVER_STATE,
          shouldOpen: false,
        });
        const _languages = _.map(languageSelections, (lang) => {
          return {
            value: lang.label,
            id: lang.id,
          };
        });
        logger.report(
          exploreResultsPageGenericClick({
            clickType: 'apply partner lng/location filter',
            sessionId,
            origin,
            entry,
            exploreVersion: 'V2',
            data: JSON.stringify({
              location: location?.address?.countryFullname,
              languageSelections: _languages,
              currentPage,
            }),
          }),
        );
      }}
      showOverflow={true}
    >
      <Box
        direction="vertical"
        paddingTop="18px"
        dataHook="professional-details-filters"
      >
        <Box direction="vertical">
          <Text size="small" className={s.locationLabel}>
            {t('explorer-filter-specific-location')}
          </Text>
          <Box display="block" marginBottom="SP6">
            <ExploreAddressInput
              value={locationInput}
              onSelectPlace={onSelectLocation}
              onClear={onClearLocation}
              onInputChange={onChangeLocation}
              statusProp={locationStatus}
            />
          </Box>
        </Box>

        <Box direction="vertical" maxWidth="350px">
          <Text size="small" className={s.languageLabel}>
            {t('explorer-filter-communication-language')}
          </Text>
          <MultiSelect
            size="small"
            predicate={languageSelectPredicate}
            tags={languageSelections}
            value={languageInput}
            options={languageOptions}
            onChange={onChangeLanguage}
            placeholder={t('explorer-filter-placeholder-language')}
            onSelect={onSelectLanguage}
            onRemoveTag={onRemoveLanguageTag}
            dataHook="language-select"
          />
        </Box>
      </Box>
    </FilterPopover>
  );
};

export default PartnerDetails;
